import { useState, type FormEvent } from 'react';
import { Wifi } from 'lucide-react';
import type { MfaChallengeView } from '@simpleacs/shared';
import { Button, Field, Input } from '../components/ui';
import { useSession } from '../lib/session';
import { DEFAULT_BRAND, usePublicBranding } from '../lib/branding';

export function LoginPage() {
  const { login } = useSession();
  const brand = usePublicBranding().data;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Set when the password was right and a second factor is outstanding.
   *
   * Held in component state and nowhere else — not in a cookie, not in
   * `localStorage`. It stands for "this browser proved a password five minutes
   * ago" and it is a credential in its own right, so it should not survive a
   * refresh or outlive the form that is using it.
   */
  const [challenge, setChallenge] = useState<MfaChallengeView | null>(null);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await login(email.trim().toLowerCase(), password);
      if (next) {
        setChallenge(next);
        setPassword('');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <LoginShell brand={brand}>
        <MfaStep
          challenge={challenge}
          onChallenge={setChallenge}
          onStartOver={() => {
            setChallenge(null);
            setError(null);
          }}
        />
      </LoginShell>
    );
  }

  return (
    <LoginShell brand={brand}>
      <form onSubmit={onSubmit} className="card space-y-4 p-6">
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@operator.net"
            required
            autoFocus
          />
        </Field>
        <Field label="Password">
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>

        {error && (
          <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Sign in
        </Button>
      </form>
    </LoginShell>
  );
}

type Brand = ReturnType<typeof usePublicBranding>['data'];

/** The logo, the name and the frame — shared by both steps of a sign-in. */
function LoginShell({ brand, children }: { brand: Brand; children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-sm">
        {/* The platform's branding, not an organization's: which org a visitor
            belongs to is not knowable until they have signed in. */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          {brand?.logoDataUri ? (
            <img src={brand.logoDataUri} alt="" className="size-12 object-contain" />
          ) : (
            <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <Wifi className="size-6" />
            </div>
          )}
          <div>
            <h1 className="text-lg font-semibold text-ink">
              {brand?.displayName || DEFAULT_BRAND.displayName}
            </h1>
            <p className="text-sm text-muted">
              {brand?.tagline || 'FTTH / PON management platform'}
            </p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The second step: the code, or a recovery code.
 *
 * One input for both. From here they are the same thing — something you type
 * that proves you are you — and offering two boxes would make somebody choose
 * which one they are holding before they have found it.
 *
 * When the code could not be SENT, the form says so and says a recovery code
 * will do. That case is not an error: the account is still protected, the
 * channel is simply down, and telling somebody "sign in failed" when their
 * password was correct and their recovery codes work is how a support call
 * becomes an outage.
 */
function MfaStep({
  challenge,
  onChallenge,
  onStartOver,
}: {
  challenge: MfaChallengeView;
  onChallenge: (next: MfaChallengeView) => void;
  onStartOver: () => void;
}) {
  const { answerMfa, resendMfa } = useSession();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await answerMfa(challenge.token, code.trim());
    } catch (err) {
      setCode('');
      setError(err instanceof Error ? err.message : 'That did not work');
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setResending(true);
    setError(null);
    try {
      onChallenge(await resendMfa(challenge.token));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send another code');
    } finally {
      setResending(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-4 p-6">
      {challenge.delivered ? (
        <p className="text-sm text-muted">
          A code was sent to <span className="font-medium text-ink">{challenge.sentTo}</span>. It
          works once and expires shortly.
        </p>
      ) : (
        <div className="space-y-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
          <p>{challenge.deliveryNote ?? 'The code could not be sent.'}</p>
          <p>
            {challenge.recoveryAvailable
              ? 'Use one of your recovery codes instead.'
              : 'You have no recovery codes left. An administrator can switch two-step sign-in off for your account.'}
          </p>
        </div>
      )}

      <Field
        label="Code"
        hint={challenge.recoveryAvailable ? 'Or one of your recovery codes' : undefined}
      >
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode={challenge.delivered ? 'numeric' : 'text'}
          autoComplete="one-time-code"
          placeholder={challenge.delivered ? '000000' : '0000-0000-0000'}
          required
          autoFocus
        />
      </Field>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <Button type="submit" variant="primary" loading={busy} className="w-full">
        Sign in
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          className="text-muted underline-offset-2 hover:underline disabled:opacity-50"
          disabled={challenge.resendsLeft === 0 || resending}
          onClick={() => void resend()}
        >
          {challenge.resendsLeft === 0 ? 'No more codes' : 'Send another code'}
        </button>
        <button
          type="button"
          className="text-muted underline-offset-2 hover:underline"
          onClick={onStartOver}
        >
          Start over
        </button>
      </div>
    </form>
  );
}
